// filepath: /Users/mk/work/2024/jimon-generator-web/src/components/JimonGenerator.tsx
import React, { useCallback, useEffect, useState, useRef } from 'react';
import LayerPanel from './LayerPanel';
import PatternPreview from './PatternPreview';
import AuthForm from './AuthForm';
import MainHeader from './MainHeader';
import {
  Layer,
  LayerParameters,
  SelectionArea,
  PreviewDimensions,
  UserPattern,
} from '../types';
import { generateSVG, downloadSVG } from '../utils/svgExport';
import { calculateScaledImageSize } from '../utils/imageScaling';
import SelectionTool from './SelectionTool';
import CoverageDisplay from './CoverageDisplay';

const MARGIN = 32; // プレビューのマージン値

const initialLayer: Layer = {
  id: '1',
  displayName: 'Layer 1', // 表示用の名前を追加
  parameters: {
    size: 60,
    gapX: 0,
    gapY: 0,
    angle: 0,
    angleView: 0,
    color: '#000000',
  },
  svgPattern: null,
  visible: true,
};

// JimonGenerator コンポーネントの定義
const JimonGenerator: React.FC = () => {
  const [layers, setLayers] = useState<Layer[]>([initialLayer]);
  const [backgroundImage, setBackgroundImage] = useState<string | null>(null);
  const [originalImageSize, setOriginalImageSize] = useState({
    width: 0,
    height: 0,
  });
  const [previewSize, setPreviewSize] = useState({ width: 0, height: 0 });
  const [lastLayerId, setLastLayerId] = useState(1);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedLayerId, setSelectedLayerId] = useState<string | undefined>(
    undefined
  );
  const [loggedInUser, setLoggedInUser] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [previewDimensions, setPreviewDimensions] =
    useState<PreviewDimensions | null>(null);
  const [generatedSvgContent, setGeneratedSvgContent] = useState<string>('');
  const [userPatterns, setUserPatterns] = useState<UserPattern[]>([]);
  const [isLoadingPatterns, setIsLoadingPatterns] = useState(false);

  // ファイル選択のための参照
  const backgroundFileInputRef = useRef<HTMLInputElement>(null);

  // 選択モードの切り替え時に選択中のレイヤーIDを設定
  const toggleSelectionMode = (layerId: string) => {
    setSelectedLayerId(layerId);
    setIsSelectionMode(true);
  };

  // セッションの状態をチェックする関数
  const checkLoginStatus = useCallback(async () => {
    try {
      setIsLoading(true);
      console.log('セッションチェックを開始...');

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/auth/session`,
        {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data?.email) {
          setLoggedInUser(data.email);
          return;
        }
      }

      // セッションが無効な場合
      setLoggedInUser('');
    } catch (error) {
      console.error('セッションチェックエラー:', error);
      setLoggedInUser('');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 初回マウント時にセッションをチェック
  useEffect(() => {
    checkLoginStatus();
  }, [checkLoginStatus]);

  const handleParameterChange = (
    layerId: string,
    paramName: keyof LayerParameters,
    value: number | string
  ) => {
    console.log('Parameter change:', { layerId, paramName, value }); // デバッグログ
    setLayers((prevLayers) =>
      prevLayers.map((layer) =>
        layer.id === layerId
          ? {
              ...layer,
              parameters: { ...layer.parameters, [paramName]: value },
            }
          : layer
      )
    );
  };

  // レイヤー追加関数を更新
  const addLayer = () => {
    // カウンターをインクリメントして新しいIDを生成
    const newId = lastLayerId + 1;
    const newLayer: Layer = {
      id: newId.toString(),
      displayName: `Layer ${newId}`, // 表示用の名前を設定
      parameters: { ...initialLayer.parameters },
      visible: true,
      svgPattern: null,
    };

    setLayers([...layers, newLayer]);
    setLastLayerId(newId); // カウンターを更新
  };

  const handleDeleteLayer = (layerId: string) => {
    if (layers.length <= 1) {
      return;
    }

    setLayers((prevLayers) =>
      prevLayers.filter((layer) => layer.id !== layerId)
    );
    // カウンターはそのままで、削除されたIDは再利用されない
  };

  const handleBackgroundUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        // 画像の実際のサイズを保存
        setOriginalImageSize({ width: img.width, height: img.height });
        setBackgroundImage(e.target?.result as string);
      };
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  };

  const calculatePreviewSize = useCallback(() => {
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const headerHeight = 48; // ヘッダーの高さは h-12 で 48px

    const previewWidth = windowWidth * 0.75; // 3/4のウィンドウ幅
    const previewHeight = windowHeight - headerHeight; // ヘッダーの高さを差し引く

    return {
      width: Math.round(previewWidth),
      height: Math.round(previewHeight),
    };
  }, []);

  const handleSvgPatternChange = (
    layerId: string,
    svgPattern: string,
    visible: boolean
  ) => {
    // 現在時刻を含む一意のIDを生成
    const uniqueId = `${layerId}_${Date.now()}`;

    setLayers((prevLayers) =>
      prevLayers.map((layer) =>
        layer.id === layerId
          ? {
              ...layer,
              svgPattern,
              visible,
              // 一意のIDに更新するが、表示名は維持
              id: uniqueId,
              displayName: layer.displayName,
            }
          : layer
      )
    );
  };

  const handleVisibilityChange = (layerId: string, visible: boolean) => {
    setLayers((prevLayers) =>
      prevLayers.map((layer) =>
        layer.id === layerId ? { ...layer, visible } : layer
      )
    );

    // レイヤーの可視性変更後にSVG内容を更新するための空の依存関係更新
    // これによりuseEffectが反応してSVG生成と隠蔽率計算が行われる
    if (previewDimensions) {
      // 少し遅延を入れてステート更新が完了してから処理されるようにする
      setTimeout(() => {
        const randomKey = Math.random().toString();
        setGeneratedSvgContent((prev) => prev + randomKey);
        setGeneratedSvgContent((prev) => prev.replace(randomKey, ''));
      }, 100);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      const newPreviewSize = calculatePreviewSize();
      setPreviewSize(newPreviewSize);
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // 初期呼び出し

    return () => window.removeEventListener('resize', handleResize);
  }, [calculatePreviewSize]);

  useEffect(() => {
    if (originalImageSize.width && originalImageSize.height) {
      // マージンを考慮した計算に変更
      const availableWidth = previewSize.width - MARGIN * 2;
      const availableHeight = previewSize.height - MARGIN * 2;

      const scaledSize = calculateScaledImageSize(
        originalImageSize.width,
        originalImageSize.height,
        availableWidth,
        availableHeight
      );

      // マージンを含めた全体サイズを設定
      setPreviewSize({
        width: scaledSize.width + MARGIN * 2,
        height: scaledSize.height + MARGIN * 2,
      });
    }
  }, [originalImageSize, previewSize.width, previewSize.height]);

  const handleExport = async () => {
    if (!previewDimensions) {
      console.error('プレビューの寸法情報が取得できていません');
      return;
    }

    try {
      const svgContent = await generateSVG(
        layers,
        previewDimensions.contentWidth,
        previewDimensions.contentHeight,
        backgroundImage
      );

      // SVG内容をステートに保存（隠蔽率計算用）
      setGeneratedSvgContent(svgContent);

      downloadSVG(svgContent, layers);
    } catch (error) {
      console.error('Error exporting SVG:', error);
    }
  };

  // SVGの生成とプレビュー更新時に隠蔽率計算用のSVG内容も更新
  useEffect(() => {
    if (!previewDimensions) return;

    const updateSvgContent = async () => {
      try {
        // 可視状態のレイヤーのみをフィルタリング
        const visibleLayers = layers.filter(
          (layer) => layer.visible && layer.svgPattern
        );

        // 表示されているレイヤーがない場合はSVG生成をスキップ
        if (visibleLayers.length === 0) {
          console.log('表示中のレイヤーがないため、SVG生成をスキップします');
          setGeneratedSvgContent('');
          return;
        }

        // 表示されているレイヤーのみを使用してSVGを生成
        const svgContent = await generateSVG(
          visibleLayers, // 可視レイヤーのみ渡す
          previewDimensions.contentWidth,
          previewDimensions.contentHeight,
          backgroundImage
        );
        setGeneratedSvgContent(svgContent);
      } catch (error) {
        console.error('SVG生成エラー:', error);
      }
    };

    updateSvgContent();
  }, [layers, previewDimensions, backgroundImage]);

  // 選択範囲の完了時の処理を更新
  const handleSelectionComplete = (area: SelectionArea, layerId: string) => {
    // 対象のレイヤーを見つける
    const layer = layers.find((layer) => layer.id === layerId);
    if (!layer || !previewDimensions) return;

    // ここでSVG要素のマージンを考慮した座標補正を行う
    // SVGコンテンツは32pxのマージンを持っているため、補正が必要
    const adjustedArea = {
      x: area.x - MARGIN, // マージン分を補正
      y: area.y - MARGIN, // マージン分を補正
      width: area.width,
      height: area.height,
    };

    // 座標補正後の範囲をクリッピングマスクとして適用
    setLayers((prevLayers) =>
      prevLayers.map((l) =>
        l.id === layerId
          ? {
              ...l,
              clipMask: adjustedArea,
            }
          : l
      )
    );
    setIsSelectionMode(false);
  };

  // レイヤーのマスクをクリアする関数を追加
  const clearLayerMask = (layerId: string) => {
    setLayers((prevLayers) =>
      prevLayers.map((layer) =>
        layer.id === layerId
          ? {
              ...layer,
              clipMask: undefined,
            }
          : layer
      )
    );
  };

  const handleMaskMove = (layerId: string, x: number, y: number) => {
    setLayers((prevLayers) =>
      prevLayers.map((layer) => {
        if (layer.id === layerId && layer.clipMask) {
          return {
            ...layer,
            clipMask: {
              ...layer.clipMask,
              x,
              y,
            },
          };
        }
        return layer;
      })
    );
  };

  const handleMaskResize = (layerId: string, newArea: SelectionArea) => {
    setLayers((prevLayers) =>
      prevLayers.map((layer) => {
        if (layer.id === layerId) {
          return {
            ...layer,
            clipMask: newArea,
          };
        }
        return layer;
      })
    );
  };

  const fetchUserPatterns = useCallback(async () => {
    if (!loggedInUser) {
      setUserPatterns([]);
      return;
    }

    setIsLoadingPatterns(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/patterns`,
        {
          credentials: 'include',
        }
      );

      if (response.ok) {
        const data = await response.json();
        // 応答データからパターンを取得し、cloudFrontUrlがあればそれを優先的に使用
        setUserPatterns(data.patterns || []);
      } else {
        console.error(
          'パターン一覧の取得に失敗しました',
          await response.text()
        );
      }
    } catch (error) {
      console.error('パターン一覧の取得エラー:', error);
    } finally {
      setIsLoadingPatterns(false);
    }
  }, [loggedInUser]);

  useEffect(() => {
    if (loggedInUser) {
      fetchUserPatterns();
    }
  }, [loggedInUser, fetchUserPatterns]);

  // パターンアップロード用の関数を追加
  const handlePatternUpload = async (file: File): Promise<boolean> => {
    try {
      // ファイルをテキストとして読み込む
      const reader = new FileReader();
      const svgContent = await new Promise<string>((resolve, reject) => {
        reader.onload = (e) => resolve(e.target?.result as string);
        reader.onerror = reject;
        reader.readAsText(file);
      });

      // APIにアップロード
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/patterns/upload`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ svgContent }),
        }
      );

      if (!response.ok) {
        console.error('アップロードに失敗しました', await response.text());
        return false;
      }

      // アップロード成功後にパターン一覧を更新
      fetchUserPatterns();
      return true;
    } catch (error) {
      console.error('アップロードエラー:', error);
      return false;
    }
  };

  // パターン削除関数
  const handlePatternDelete = async (patternId: string): Promise<boolean> => {
    if (!loggedInUser) return false;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/patterns/${patternId}`,
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );

      if (response.ok) {
        // 削除成功した場合、パターン一覧を更新
        fetchUserPatterns();
        return true;
      } else {
        console.error('パターン削除に失敗しました', await response.text());
        return false;
      }
    } catch (error) {
      console.error('パターン削除エラー:', error);
      return false;
    }
  };

  // 背景画像アップロードのハンドラー（ヘッダーからの呼び出し用）
  const handleUploadBackgroundClick = () => {
    if (backgroundFileInputRef.current) {
      backgroundFileInputRef.current.click();
    }
  };

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      {isLoading ? (
        <div className="w-full h-full flex items-center justify-center bg-gray-100">
          <div className="text-xl">読み込み中...</div>
        </div>
      ) : !loggedInUser ? (
        <div className="w-full h-full flex items-center justify-center bg-gray-100">
          <div className="w-96">
            <AuthForm onLogin={setLoggedInUser} />
          </div>
        </div>
      ) : (
        <>
          {/* メインヘッダー */}
          <MainHeader
            onUploadBackground={handleUploadBackgroundClick}
            onAddLayer={addLayer}
            onExport={handleExport}
          />

          <div className="flex flex-1 overflow-hidden">
            {/* コントロールパネル（左側） */}
            <div className="w-1/4 bg-gray-100 flex flex-col">
              {/* 固定エリア：隠蔽率表示 */}
              <div className="flex-shrink-0 p-4 border-b border-gray-300">
                {/* 隠しファイル入力 */}
                <input
                  type="file"
                  ref={backgroundFileInputRef}
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      handleBackgroundUpload(e.target.files[0]);
                    }
                  }}
                  accept="image/png, image/jpeg" // pngとjpeg形式のみに指定
                  className="hidden"
                />

                {/* 隠蔽率表示 */}
                {generatedSvgContent && previewDimensions && (
                  <div className="mb-0">
                    <h3 className="text-sm font-bold mb-2">隠蔽率</h3>
                    <CoverageDisplay
                      svgContent={generatedSvgContent}
                      width={previewDimensions.width}
                      height={previewDimensions.height}
                      backgroundImage={backgroundImage}
                      updateRate={2000} // 2秒ごとに更新
                      layers={layers} // レイヤー情報を渡す
                    />
                  </div>
                )}
              </div>

              {/* スクロール可能エリア：レイヤーパネル群 */}
              <div className="flex-1 overflow-y-auto p-4">
                {layers.map((layer) => (
                  <LayerPanel
                    key={layer.id}
                    layer={layer}
                    onParameterChange={handleParameterChange}
                    onSvgPatternChange={handleSvgPatternChange}
                    onVisibilityChange={handleVisibilityChange}
                    onDeleteLayer={handleDeleteLayer}
                    onClearMask={clearLayerMask}
                    onSelectForMask={() => toggleSelectionMode(layer.id)}
                    userPatterns={userPatterns}
                    isLoadingPatterns={isLoadingPatterns}
                    onPatternDelete={handlePatternDelete}
                    onPatternUpload={handlePatternUpload} // アップロードハンドラを追加
                  />
                ))}

                {isSelectionMode && (
                  <div className="mt-4">
                    <button
                      onClick={() => setIsSelectionMode(false)}
                      className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded mr-2"
                    >
                      Cancel Selection
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* プレビュー画面（右側） */}
            <div className="w-3/4 bg-white overflow-hidden relative">
              <PatternPreview
                layers={layers}
                width={previewSize.width}
                height={previewSize.height}
                backgroundImage={backgroundImage}
                isSelectionMode={isSelectionMode}
                onSelectionComplete={handleSelectionComplete}
                selectedLayerId={selectedLayerId}
                onMaskMove={handleMaskMove}
                onMaskResize={handleMaskResize}
                onDimensionsChange={setPreviewDimensions}
                svgContent={generatedSvgContent}
              />
              {isSelectionMode && (
                <SelectionTool
                  onSelectionComplete={handleSelectionComplete}
                  width={previewSize.width}
                  height={previewSize.height}
                  selectedLayerId={selectedLayerId}
                />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default JimonGenerator;
